import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type {
  EmployeeDetail,
  EmployeeSummary,
  PageResponse,
  RoleView,
} from "../../shared/types/api";

const schema = z.object({
  employeeCode: z
    .string()
    .min(1, "Enter a code")
    .max(32, "Keep it under 32 characters")
    .regex(/^[A-Za-z0-9_-]+$/, "Letters, digits, hyphens and underscores only"),
  fullName: z.string().min(1, "Enter their name").max(200),
  officialEmail: z.string().min(1, "Enter their email").email("That does not look like an email"),
  designation: z.string().min(1, "Enter a designation").max(100),
  department: z.string().min(1, "Enter a department").max(100),
  dateOfJoining: z.string().min(1, "Choose a joining date"),
  phone: z.string().max(32).optional(),
  reportingManagerId: z.string().optional(),
  role: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Creates an employee and sends their invitation.
 *
 * <p>The panel says plainly that an email goes out. Creating a record and silently notifying a
 * real person are different things, and whoever clicks Create should know both are happening.
 */
export function EmployeeFormDrawer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (employee: EmployeeDetail) => void;
}) {
  const { can } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);

  const managers = useQuery({
    queryKey: ["employees", "managers"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<RoleView[]>("/api/v1/roles"),
    enabled: open && can("ROLE_MANAGE"),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      dateOfJoining: new Date().toISOString().slice(0, 10),
      role: "EMPLOYEE",
    },
  });

  const close = () => {
    reset();
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const created = await api.post<EmployeeDetail>("/api/v1/employees", {
        ...values,
        reportingManagerId: values.reportingManagerId || undefined,
        roles: values.role ? [values.role] : undefined,
        role: undefined,
      });
      onSaved(created);
      close();
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.code === "DUPLICATE_RESOURCE"
          ? "Someone already has that email address or employee code."
          : error instanceof ApiError
            ? error.message
            : "Could not create the employee.",
      );
    }
  });

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Add an employee"
      subtitle="They receive an invitation and choose their own password."
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Creating…" : "Create and invite"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <InlineAlert tone="info">
          An invitation is emailed to the address below. Nobody else ever sets their password for
          them.
        </InlineAlert>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Full name" required error={errors.fullName?.message}>
            <Input autoFocus {...register("fullName")} />
          </Field>
          <Field label="Employee code" required error={errors.employeeCode?.message}>
            <Input placeholder="EMP005" {...register("employeeCode")} />
          </Field>
        </div>

        <Field
          label="Official email"
          required
          error={errors.officialEmail?.message}
          hint="The invitation goes here."
        >
          <Input type="email" {...register("officialEmail")} />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Designation" required error={errors.designation?.message}>
            <Input {...register("designation")} />
          </Field>
          <Field label="Department" required error={errors.department?.message}>
            <Input {...register("department")} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Date of joining" required error={errors.dateOfJoining?.message}>
            <Input type="date" {...register("dateOfJoining")} />
          </Field>
          <Field label="Phone" hint="Optional">
            <Input {...register("phone")} />
          </Field>
        </div>

        <Field label="Reporting manager" hint="Optional">
          <Select {...register("reportingManagerId")} disabled={managers.isPending}>
            <option value="">No manager</option>
            {(managers.data?.content ?? []).map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.fullName} ({manager.employeeCode})
              </option>
            ))}
          </Select>
        </Field>

        {can("ROLE_MANAGE") && (
          <Field
            label="Role"
            hint="EMPLOYEE is the baseline everyone should have. More can be added later."
          >
            <Select {...register("role")} disabled={roles.isPending}>
              {(roles.data ?? []).map((role) => (
                <option key={role.code} value={role.code}>
                  {role.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </form>
    </Drawer>
  );
}
